import { Box, styled, useMediaQuery, useTheme } from "@mui/material";
import { useDataGrid } from "hooks/useDataGrid";
import { useMemo, useState } from "react";
import { Spinner } from "UI/Spinner";
import { DataGrid } from "./DataGrid";
import { ErrorWindow } from "./ErrorWindow";
import { TextFieldWithEllipsis } from "./TextFieldWithEllipsis";
import Status from "./Status";
import { ReactComponent as AttachmentsIcon } from "images/attachments-icon.svg";
import { ReactComponent as AttachmentsActiveIcon } from "images/attachments-icon-active.svg";
import { Stack, Tooltip } from "@mui/material";
import { customColors } from "models/customColors";
import { displayDobTime, displayLocalDateTime } from "utils";

export const Title = styled(Box)`
  font-family: sans-serif;
  font-size: 26px;
  font-weight: 500;
  line-height: 31px;
  letter-spacing: 0em;
  text-align: left;
`;

export const statusSx = {
  referred: {
    color: "#0085FF",
    backgroundColor: "#0085FF1A",
    "&:hover": {
      backgroundColor: "#0085FF2A",
    },
  },
  acknowledged: {
    color: "#00C1CA",
    backgroundColor: "#E8FEFF",
    "&:hover": {
      backgroundColor: "#D8EEEF",
    },
  },
  contacted: {
    color: "#514EF3",
    backgroundColor: "#ECECFE",
    "&:hover": {
      backgroundColor: "#CCCCCE",
    },
  },
  scheduled: {
    color: "#FF7A00",
    backgroundColor: "#FEF8EC",
    "&:hover": {
      backgroundColor: "#EEE8DC",
    },
  },
  unsuccessful: {
    color: "#FF472E",
    backgroundColor: "#FFEDEA",
    "&:hover": {
      backgroundColor: "#EFDDDA",
    },
  },
  completed: {
    color: "#1DA633",
    backgroundColor: "#EAFBE7",
    "&:hover": {
      backgroundColor: "#DAEBD7",
    },
  },
};

const columns = [
  {
    label: "ID",
    field: "id",
    xs: 1.5,
    sm: 1.5,
    md: 1.5,
    renderer: ({ id }) => id.substr(0, 8),
  },
  {
    label: "Created At",
    field: "createdAt",
    xs: 2,
    sm: 2,
    md: 2,
    renderer: ({ createdAt }) => {
      const date = displayLocalDateTime(createdAt);
      return (
        <TextFieldWithEllipsis withTooltip>
          {date}
        </TextFieldWithEllipsis>
      );
    },
  },
  // {
  //   label: "Type",
  //   field: "type",
  //   xs: 1.25,
  //   sm: 1.25,
  //   md: 1.25,
  //   renderer: ({ type }) => (
  //     <TextFieldWithEllipsis withTooltip>{type}</TextFieldWithEllipsis>
  //   ),
  // },
  {
    label: "Provider",
    field: "provider",
    xs: 1.25,
    sm: 1.25,
    md: 1.25,
    renderer: ({ provider }) => (
      <TextFieldWithEllipsis withTooltip>{provider}</TextFieldWithEllipsis>
    ),
  },
  {
    label: "Recipient",
    field: "recipientName",
    xs: 1.75,
    sm: 1.75,
    md: 1.75,
    renderer: ({ recipientName }) => (
      <TextFieldWithEllipsis withTooltip>{recipientName}</TextFieldWithEllipsis>
    ),
  },
  {
    label: "Patient",
    field: "patientName",
    xs: 1.75,
    sm: 1.75,
    md: 1.75,
    renderer: ({ patientName }) => (
      <TextFieldWithEllipsis withTooltip>{patientName}</TextFieldWithEllipsis>
    ),
  },
  {
    label: "Patient DOB",
    field: "patientDob",
    xs: 1.25,
    sm: 1.25,
    md: 1.25,
    renderer: ({ patientDob }) => displayDobTime(patientDob),
  },
  {
    label: (
      <Tooltip title="Attachments">
        <AttachmentsIcon />
      </Tooltip>
    ),
    field: "attachments",
    xs: 0.5,
    sm: 0.5,
    md: 0.5,
    renderer: ({ attachments }) =>
      attachments && (
        <Stack
          direction="row"
          gap={0.35}
          sx={{ color: customColors.mainMessageBlue }}
        >
          <AttachmentsActiveIcon /> {attachments ?? 0}
        </Stack>
      ),
  },
  {
    label: "Status",
    field: "status",
    xs: 2,
    sm: 2,
    md: 2,
    renderer: ({ status }) => <Status value={status} />,
  },
];

const mobileColumns = [
  {
    label: "Created At",
    field: "createdAt",
    xs: 4,
    sm: 4,
    md: 4,
    renderer: ({ createdAt }) => {
      const date = displayLocalDateTime(createdAt);
      return (
        <TextFieldWithEllipsis withTooltip>
          {date}
        </TextFieldWithEllipsis>
      );
    },
  },
  {
    label: "Recipient",
    field: "recipientName",
    xs: 2.5,
    sm: 2.5,
    md: 2.5,
    renderer: ({ recipientName }) => (
      <TextFieldWithEllipsis withTooltip>{recipientName}</TextFieldWithEllipsis>
    ),
  },
  {
    label: "Patient",
    field: "patientName",
    xs: 3,
    sm: 3,
    md: 3,
    renderer: ({ patientName }) => (
      <TextFieldWithEllipsis withTooltip>{patientName}</TextFieldWithEllipsis>
    ),
  },
  {
    label: "Status",
    field: "status",
    xs: 2.5,
    sm: 2.5,
    md: 2.5,
    renderer: ({ status }) => <Status value={status} />,
  },
];

export const Referrals = ({
  originator,
  setSelectedReferralId,
}) => {
  const [loading, setLoading] = useState(true);
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));

  const additionalParams = useMemo(
    () => ({
      currentStatus: "All",
      dashboardAccessToken: originator?.dashboardAccessToken,
    }),
    [originator]
  );

  const dataGrid = useDataGrid({
    url: `${process.env.REACT_APP_API_URL}/originator_referrals`,
    defaultSorter: {
      name: "createdAt",
      order: "desc",
    },
    defaultLimit: 25,
    gridConfigIndex: `${originator?.id}-referrals`,
    additionalParams,
    reloadIntervalInSeconds: 15,
    setLoading,
  });

  const handelRowClick = (row) => {
    setSelectedReferralId(row?.id);
  };

  return (
    <Box flex={1}>
      <DataGrid
        columns={isMobile ? mobileColumns : columns}
        handleRowClick={handelRowClick}
        {...dataGrid}
      />
      {loading && <Spinner />}
      {dataGrid?.error && (
        <ErrorWindow
          error={dataGrid?.error}
          handleClose={() => dataGrid?.setError("")}
        />
      )}
    </Box>
  );
};
